<template>
  <Transition>
    <TopBanner :mode="bannerMode" :text="bannerText" v-if="bannerText" />
  </Transition>

  <!-- Time Account Modal -->
  <div v-if="showTimeAccountModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div class="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto m-4">
      <div class="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
        <h2 class="text-xl font-bold">Zeitkonto - {{ selectedEmployee?.name }} {{ selectedEmployee?.surname }}</h2>
        <button type="button" @click="closeTimeAccountModal" class="admin-button--ghost admin-button--icon" aria-label="Schließen">
          <i class="fa fa-times text-xl"></i>
        </button>
      </div>
      <div class="p-4">
        <TimeAccountTab v-if="selectedEmployee" :user="selectedEmployee" :month="selectedMonth" @changed="modalChanged = true" />
      </div>
    </div>
  </div>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <h1 class="admin-page-title">Zeitkonten</h1>
        </div>
      </div>

        <div class="mt-4 mb-4 flex justify-center items-center gap-4">
          <label class="font-bold">Monat/Jahr:</label>
          <input v-model="selectedMonth" type="month" class="border-2 border-gray-500 rounded px-3 py-2"
            @change="onMonthChange" />
        </div>
        <div class="mx-4 mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <label for="zeitkonten-employee-filter" class="shrink-0 font-bold">
            Mitarbeiter filtern:
          </label>
          <div class="relative w-full sm:max-w-md">
            <input
              id="zeitkonten-employee-filter"
              v-model="employeeFilter"
              type="search"
              placeholder="Name oder Personalnummer"
              class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 text-sm text-gray-800 shadow-sm"
            />
            <button
              v-if="employeeFilter"
              type="button"
              class="admin-button--ghost admin-button--icon absolute right-1 top-1/2 -translate-y-1/2 !h-8 !min-h-8 !w-8 !min-w-8"
              aria-label="Mitarbeiterfilter löschen"
              @click="employeeFilter = ''"
            >
              <i class="fa fa-xmark"></i>
            </button>
          </div>
        </div>

        <!-- Zeitkonten Overview -->
        <div class="mt-4 mb-4 bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mx-4">
          <div class="text-left mb-3">
            <span class="font-bold text-lg">Zeitkonten</span>
            <span class="ml-2 text-sm text-gray-600">(Stunden und Geldmittel der Zeitkonten)</span>
          </div>
          <div v-if="loading && loadedCount < totalCount"
            class="flex items-center gap-3">
            <i class="fa fa-spinner fa-spin text-xl text-blue-600"></i>
            <span class="text-gray-600">Lade Zeitkonten... {{ loadedCount }} von {{ totalCount }}</span>
          </div>
          <div v-else>
            <div class="grid grid-cols-2 gap-4 text-left mb-3">
              <div class="bg-white rounded-lg p-3 border border-blue-200">
                <div class="text-sm text-gray-600">Summe Stunden (abgerechnet)</div>
                <div class="text-xl font-bold text-blue-600">{{ totalBilledHours.toFixed(2) }}h</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-green-200">
                <div class="text-sm text-gray-600">Summe aller Stunden</div>
                <div class="text-xl font-bold text-green-600">{{ totalAllHours.toFixed(2) }}h</div>
                <div class="text-sm text-gray-500">{{ (totalAllHours * 15.11).toFixed(2) }}€</div>
              </div>
            </div>

            <div v-if="filteredEmployees.length > 0" class="overflow-y-auto max-h-96 rounded-lg border border-blue-200">
              <table class="w-full text-sm">
                <thead class="bg-blue-200 text-blue-900 sticky top-0">
                  <tr>
                    <th class="py-2 px-3 font-semibold text-left">Personalnummer</th>
                    <th class="py-2 px-3 font-semibold text-left">Name</th>
                    <th class="py-2 px-3 font-semibold text-right">Soll-Stunden</th>
                    <th class="py-2 px-3 font-semibold text-right">Gearbeitet</th>
                    <th class="py-2 px-3 font-semibold text-right">Geplant</th>
                    <th class="py-2 px-3 font-semibold text-right">Abgerechnet</th>
                    <th class="py-2 px-3 font-semibold text-right">Startsaldo</th>
                    <th class="py-2 px-3 font-semibold text-right">Autokorrektur</th>
                    <th class="py-2 px-3 font-semibold text-right">Korrektur</th>
                    <th class="py-2 px-3 font-semibold text-right">Endsaldo</th>
                    <th class="py-2 px-3 font-semibold text-center">Abgerechnet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="employee in filteredEmployees" :key="employee.id" class="border-b border-blue-100 hover:bg-blue-50">
                    <td class="py-2 px-3 font-bold text-blue-600 underline cursor-pointer" @click="openTimeAccountModal(employee)">{{ employee.persnr }}</td>
                    <td class="py-2 px-3">{{ employee.name }} {{ employee.surname }}</td>
                    <td class="py-2 px-3 text-right">{{ employee.monthlyHours || 0 }}h</td>
                    <td class="py-2 px-3 text-right">
                      <span v-if="employee.timeAccountData">{{
                        employee.timeAccountData.calculations.workedHours.toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right">
                      <span v-if="employee.timeAccountData">{{
                        employee.timeAccountData.calculations.plannedHours.toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right">
                      <span v-if="employee.timeAccountData">{{
                        calculateBilledHours(employee).toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right">
                      <span v-if="employee.timeAccountData">{{
                        employee.timeAccountData.timeAccount.startBalance.toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right">
                      <span v-if="employee.timeAccountData">{{
                        employee.timeAccountData.timeAccount.autoCorrection.toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right">
                      <input v-if="employee.timeAccountData && employee.timeAccountData.timeAccount.endBalance === null"
                        v-model.number="employee.manualCorrection" type="number" step="0.5"
                        @change="updateManualCorrection(employee)" class="border rounded px-2 py-1 w-20 text-right"
                        placeholder="0" />
                      <span v-else-if="employee.timeAccountData" class="font-semibold">
                        {{ (employee.timeAccountData.timeAccount.manualCorrection || 0).toFixed(2) }}h
                      </span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-right font-bold"
                      :class="employee.timeAccountData && calculateEndBalance(employee) < 0 ? 'text-red-600' : 'text-green-600'">
                      <span v-if="employee.timeAccountData">{{ calculateEndBalance(employee).toFixed(2) }}h</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td class="py-2 px-3 text-center">
                      <button v-if="employee.timeAccountData && employee.timeAccountData.timeAccount.endBalance === null"
                        type="button"
                        @click="billAccount(employee)"
                        class="admin-button--primary admin-button--sm inline-flex items-center gap-1">
                        <i class="fa fa-calculator"></i>Abrechnen
                      </button>
                      <span v-else-if="employee.timeAccountData"
                        class="px-3 py-1 bg-green-100 text-green-800 rounded font-semibold">
                        Abgerechnet
                      </span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-gray-500 py-4">
                <span>Keine passenden Mitarbeiter mit monatlichen Stunden gefunden.</span>
            </div>

            <div class="mt-3 flex justify-center">
              <button @click="billAllAccounts"
                type="button"
                class="admin-button--primary"
                :disabled="loading || filteredEmployees.length === 0">
                <i class="fa fa-calculator mr-2"></i>Alle abrechnen
              </button>
            </div>
          </div>
        </div>

        <!-- Vacation Overview -->
        <div class="mt-4 mb-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4 mx-4">
          <div class="text-left mb-3">
            <span class="font-bold text-lg">Urlaubsrückstellung</span>
            <span class="ml-2 text-sm text-gray-600">(Geld, das für Urlaubsansprüche zurückgelegt werden muss)</span>
          </div>
          <div v-if="vacationLoading && vacationLoadedCount < vacationTotalCount"
            class="flex items-center gap-3">
            <i class="fa fa-spinner fa-spin text-xl text-purple-600"></i>
            <span class="text-gray-600">Lade Urlaubsdaten... {{ vacationLoadedCount }} von {{ vacationTotalCount }}</span>
          </div>
          <div v-else>
            <div class="grid grid-cols-3 gap-4 text-left mb-3">
              <div class="bg-white rounded-lg p-3 border border-purple-200">
                <div class="text-sm text-gray-600">Urlaub bereits ausgezahlt</div>
                <div class="text-xl font-bold text-purple-600">{{ totalVacationTakenHours.toFixed(2) }}h</div>
                <div class="text-sm text-gray-500">{{ totalVacationTakenAmount.toFixed(2) }}€</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-red-200">
                <div class="text-sm text-gray-600">Urlaub ausstehend (Rückstellung)</div>
                <div class="text-xl font-bold text-red-600">{{ totalVacationRemainingHours.toFixed(2) }}h</div>
                <div class="text-sm text-gray-500">{{ totalVacationRemainingAmount.toFixed(2) }}€</div>
              </div>
              <div class="bg-white rounded-lg p-3 border border-gray-200">
                <div class="text-sm text-gray-600">Gesamt (Urlaubsanspruch)</div>
                <div class="text-xl font-bold text-gray-800">{{ totalVacationEntitlementHours.toFixed(2) }}h</div>
                <div class="text-sm text-gray-500">{{ totalVacationEntitlementAmount.toFixed(2) }}€</div>
              </div>
            </div>

            <div v-if="filteredVacationDataWithRemaining.length > 0" class="overflow-y-auto max-h-96 rounded-lg border border-purple-200">
              <table class="w-full text-sm">
                <thead class="bg-purple-200 text-purple-900 sticky top-0">
                  <tr>
                    <th class="py-2 px-3 font-semibold text-left">Personalnummer</th>
                    <th class="py-2 px-3 font-semibold text-left">Name</th>
                    <th class="py-2 px-3 font-semibold text-right">Tage/Jahr</th>
                    <th class="py-2 px-3 font-semibold text-right">Resturlaub VJ</th>
                    <th class="py-2 px-3 font-semibold text-right">Std/Tag</th>
                    <th class="py-2 px-3 font-semibold text-right">Genommene Tage</th>
                    <th class="py-2 px-3 font-semibold text-right">Außerhalb genommene Tage</th>
                    <th class="py-2 px-3 font-semibold text-right">Ausstehende Tage</th>
                    <th class="py-2 px-3 font-semibold text-right">Ausstehende Std</th>
                    <th class="py-2 px-3 font-semibold text-right">Rückstellung (€)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="emp in filteredVacationDataWithRemaining" :key="emp.userId" class="border-b border-purple-100 hover:bg-purple-50">
                    <td class="py-2 px-3 font-bold">{{ emp.persnr }}</td>
                    <td class="py-2 px-3">{{ emp.name }} {{ emp.surname }}</td>
                    <td class="py-2 px-3 text-right">{{ emp.vacationDaysThisYear }}</td>
                    <td class="py-2 px-3 text-right">{{ emp.vacationDaysLastYear }}</td>
                    <td class="py-2 px-3 text-right">{{ emp.vacationHoursPerDay.toFixed(2) }}</td>
                    <td class="py-2 px-3 text-right text-purple-600">{{ emp.takenDays }}</td>
                    <td class="py-2 px-3 text-right text-orange-600">{{ Number(emp.vacationDaysTakenOutside || 0).toFixed(1) }}</td>
                    <td class="py-2 px-3 text-right font-bold text-red-600">{{ emp.remainingDays.toFixed(1) }}</td>
                    <td class="py-2 px-3 text-right font-bold text-red-600">{{ emp.remainingHours.toFixed(2) }}h</td>
                    <td class="py-2 px-3 text-right font-bold text-red-600">{{ emp.remainingAmount.toFixed(2) }}€</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else-if="!vacationLoading" class="text-center text-gray-500 py-4">
              <span>Keine ausstehenden Urlaubsstunden.</span>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import CASP from "@/CASP.js";
import TopBanner from "@/components/TopBanner.vue";
import TimeAccountTab from "@/components/TimeAccountTab.vue";

export default {
  name: 'Zeitkonten',
  components: { TopBanner, TimeAccountTab },
  data() {
    return {
      selectedMonth: new Date().toISOString().slice(0, 7),
      employeeFilter: '',
      employees: [],
      loading: false,
      loadedCount: 0,
      totalCount: 0,
      bannerText: '',
      bannerMode: 'success',
      showTimeAccountModal: false,
      selectedEmployee: null,
      modalChanged: false,
      vacationData: [],
      vacationLoading: false,
      vacationLoadedCount: 0,
      vacationTotalCount: 0
    };
  },
  computed: {
    filteredEmployees() {
      return this.employees.filter(employee => this.matchesEmployeeFilter(employee));
    },
    totalBilledHours() {
      return this.filteredEmployees.reduce((sum, emp) => {
        if (emp.timeAccountData && emp.timeAccountData.timeAccount.endBalance !== null) {
          return sum + this.calculateBilledHours(emp);
        }
        return sum;
      }, 0);
    },
    totalAllHours() {
      return this.filteredEmployees.reduce((sum, emp) => {
        if (emp.timeAccountData) {
          return sum + Number(emp.timeAccountData.calculations.workedHours || 0);
        }
        return sum;
      }, 0);
    },
    totalVacationTakenHours() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.takenHours || 0), 0);
    },
    totalVacationTakenAmount() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.takenAmount || 0), 0);
    },
    totalVacationRemainingHours() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.remainingHours || 0), 0);
    },
    totalVacationRemainingAmount() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.remainingAmount || 0), 0);
    },
    totalVacationOutsideHours() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.outsideHours || 0), 0);
    },
    totalVacationOutsideAmount() {
      return this.filteredVacationData.reduce((sum, emp) => sum + (emp.outsideAmount || 0), 0);
    },
    totalVacationEntitlementHours() {
      return this.totalVacationTakenHours + this.totalVacationRemainingHours + this.totalVacationOutsideHours;
    },
    totalVacationEntitlementAmount() {
      return this.totalVacationTakenAmount + this.totalVacationRemainingAmount + this.totalVacationOutsideAmount;
    },
    filteredVacationData() {
      return this.vacationData.filter(employee => this.matchesEmployeeFilter(employee));
    },
    filteredVacationDataWithRemaining() {
      return this.filteredVacationData.filter(emp =>
        emp.remainingHours > 0 || emp.vacationDaysTakenOutside > 0
      );
    }
  },
  methods: {
    matchesEmployeeFilter(employee) {
      const query = this.employeeFilter.trim().toLocaleLowerCase();
      if (!query) return true;
      return [employee.name, employee.surname, employee.persnr]
        .map(value => String(value || '').toLocaleLowerCase())
        .some(value => value.includes(query));
    },
    onMonthChange() {
      this.loadEmployees();
      this.loadVacationOverview();
    },
    calculateEndBalance(employee) {
      if (!employee.timeAccountData) return 0;
      return Number(employee.timeAccountData.calculations.endBalance || 0);
    },
    calculateBilledHours(employee) {
      if (!employee.timeAccountData) return 0;
      return Number(employee.timeAccountData.calculations.billedHours || 0);
    },
    async loadEmployees() {
      this.loading = true;
      this.loadedCount = 0;
      this.employees = [];

      try {
        // Fetch all users with monthlyHours != 0
        CASP.getUsersWithFilter({ monthlyHours: "some" }, (response) => {

          let relevantEmployees = response

          this.totalCount = relevantEmployees.length;

          // Load time account data for each employee one by one
          relevantEmployees.forEach((employee, index) => {
            this.loadTimeAccountForEmployee(employee, index === relevantEmployees.length - 1);
          });

        });
      }
      catch (error) {
        console.error('Error loading employees:', error);
        this.showBanner('Fehler beim Laden der Mitarbeiter', 'error');
        this.loading = false;
      }
    },
    loadTimeAccountForEmployee(employee, isLast) {
      CASP.getTimeAccount(employee.id, this.selectedMonth, (status, response) => {
        if (status === 200 && response) {
          employee.timeAccountData = response;
          employee.manualCorrection = response.timeAccount.manualCorrection || 0;
          this.employees.push(employee);
        } else {
          // Even if time account fails, add employee with no data
          employee.timeAccountData = null;
          employee.manualCorrection = 0;
          this.employees.push(employee);
        }

        this.loadedCount++;

        if (isLast) {
          this.loading = false;
        }
      });
    },
    updateManualCorrection(employee) {
      if (!employee.timeAccountData) return;

      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.updateTimeAccountStartBalance(
        employee.id,
        this.selectedMonth,
        employee.timeAccountData.timeAccount.startBalance,
        employee.manualCorrection,
        (status, response) => {
          document.getElementById("mainspinner").style.display = "none";
          if (status === 200) {
            employee.timeAccountData.timeAccount.manualCorrection = employee.manualCorrection;
            this.showBanner('Korrektur gespeichert', 'success');
            this.loadEmployees();
          } else {
            this.showBanner(this.getApiMessage(response, 'Fehler beim Speichern der Korrektur'), 'error');
          }
        }
      );
    },
    requestCalculation(employee, confirmed = false) {
      return new Promise(resolve => {
        CASP.calculateTimeAccount(employee.id, this.selectedMonth, confirmed, (status, response) => {
          resolve({ status, response });
        });
      });
    },
    async billAccount(employee) {
      if (!employee.timeAccountData) return;

      document.getElementById("mainspinner").style.display = "inline-block";
      let result = await this.requestCalculation(employee);
      if (result.status === 409 && result.response && result.response.requiresConfirmation &&
        window.confirm(result.response.error)) {
        result = await this.requestCalculation(employee, true);
      }
      document.getElementById("mainspinner").style.display = "none";
      if (result.status === 200) {
        CASP.getTimeAccount(employee.id, this.selectedMonth, (status, response) => {
          if (status === 200 && response) {
            employee.timeAccountData = response;
            employee.manualCorrection = response.timeAccount.manualCorrection || 0;
            this.showBanner(`Zeitkonto für ${employee.name} ${employee.surname} abgerechnet`, 'success');
          } else {
            this.showBanner(this.getApiMessage(response, 'Fehler beim Aktualisieren'), 'error');
          }
        }
        );
      } else {
        this.showBanner(this.getApiMessage(result.response, 'Fehler beim Abrechnen'), 'error');
      }
    },
    async billAllAccounts() {
      const unbilledEmployees = this.filteredEmployees.filter(emp =>
        emp.timeAccountData && emp.timeAccountData.timeAccount.endBalance === null
      );

      if (unbilledEmployees.length === 0) {
        this.showBanner('Keine unbezahlten Zeitkonten gefunden', 'info');
        return;
      }

      if (!confirm(`Möchten Sie wirklich ${unbilledEmployees.length} Zeitkonten abrechnen?`)) {
        return;
      }

      document.getElementById("mainspinner").style.display = "inline-block";

      let processed = 0;
      let confirmationGranted = false;
      const failures = [];
      for (const employee of unbilledEmployees) {
        let result = await this.requestCalculation(employee, confirmationGranted);
        if (result.status === 409 && result.response && result.response.requiresConfirmation &&
          window.confirm(result.response.error)) {
          confirmationGranted = true;
          result = await this.requestCalculation(employee, true);
        }
        processed++;
        if (result.status === 409 && result.response && result.response.requiresConfirmation) {
          failures.push(`${employee.name} ${employee.surname}: DATEV-Bestätigung abgelehnt`);
          break;
        }
        if (result.status !== 200) {
          failures.push(`${employee.name} ${employee.surname}: ${this.getApiMessage(result.response, 'Fehler beim Abrechnen')}`);
          continue;
        }
        await new Promise(resolve => {
          CASP.getTimeAccount(employee.id, this.selectedMonth, (status, response) => {
            if (status === 200 && response) {
              employee.timeAccountData = response;
              employee.manualCorrection = response.timeAccount.manualCorrection || 0;
            } else {
              failures.push(`${employee.name} ${employee.surname}: ${this.getApiMessage(response, 'Fehler beim Aktualisieren')}`);
            }
            resolve();
          });
        });
      }

      document.getElementById("mainspinner").style.display = "none";
      const successful = unbilledEmployees.filter(employee =>
        employee.timeAccountData && employee.timeAccountData.timeAccount.endBalance !== null
      ).length;
      if (failures.length > 0) {
        this.showBanner(`${successful} von ${processed} abgerechnet. ${failures.join(' | ')}`, 'error');
      } else {
        this.showBanner(`${successful} von ${processed} Zeitkonten abgerechnet`, 'success');
      }
    },
    openTimeAccountModal(employee) {
      this.selectedEmployee = employee;
      this.showTimeAccountModal = true;
    },
    closeTimeAccountModal() {
      this.showTimeAccountModal = false;
      this.selectedEmployee = null;
      if (this.modalChanged) {
        this.modalChanged = false;
        this.loadEmployees();
      }
    },
    getApiMessage(response, fallback) {
      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (error) {
          return response || fallback;
        }
      }
      return response && (response.error || response.desc) || fallback;
    },
    loadVacationOverview() {
      this.vacationLoading = true;
      this.vacationLoadedCount = 0;
      this.vacationData = [];

      CASP.getUsersWithFilter({ monthlyHours: "some" }, (response) => {
        if (!response || !Array.isArray(response)) {
          this.vacationLoading = false;
          return;
        }

        this.vacationTotalCount = response.length;

        if (response.length === 0) {
          this.vacationLoading = false;
          return;
        }

        response.forEach((employee, index) => {
          this.loadVacationForEmployee(employee, index === response.length - 1);
        });
      });
    },
    loadVacationForEmployee(employee, isLast) {
      CASP.getVacationOverviewForUser(employee.id, this.selectedMonth, (status, response) => {
        if (status === 200 && response) {
          this.vacationData.push(response);
        }

        this.vacationLoadedCount++;

        if (isLast) {
          this.vacationLoading = false;
        }
      });
    },
    showBanner(text, mode = 'success') {
      this.bannerText = text;
      this.bannerMode = mode;
      setTimeout(() => {
        this.bannerText = '';
      }, 3000);
    }
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
    if (localStorage) localStorage.setItem("last", "/admin/zeitkonten");
    this.loadEmployees();
    this.loadVacationOverview();
  }
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
}
</style>
